/**
 * Reduce a URL to a safe descriptor for error reporting: keep the origin and
 * path (which identify the resource) but drop anything that can carry secrets —
 * userinfo credentials and the query/hash, where signed-URL signatures, tokens
 * and cache-busting params live. `data:` URLs are collapsed to `data:[inline]`
 * so an inline payload is never echoed back. Relative or otherwise unparseable
 * inputs fall back to a best-effort strip of any authority userinfo, query and
 * hash.
 *
 * @param value A URL or URL-like string.
 * @returns A safe descriptor: `data:[inline]`, or the origin+path with
 *   credentials, query and hash removed.
 */
export default function sanitizeUrl(value: string): string {
	/*
	 * Browser URL parsing trims surrounding whitespace before parsing, so match
	 * that here; otherwise a `   //user:pass@host` input would slip past both
	 * the data: check and the fallback authority regex.
	 */
	const trimmed = value.trim();

	/*
	 * A data: URL parses successfully but keeps its whole (potentially
	 * multi-megabyte or sensitive) payload in the pathname, so clearing
	 * search/hash would still echo it back. Collapse it instead. The scheme is
	 * case-insensitive. This lives here, not only in describeImageSource, so
	 * direct callers such as fontLoadError are covered too.
	 */
	if (/^data:/i.test(trimmed)) {
		return 'data:[inline]';
	}

	try {
		const url = new URL(trimmed);

		url.username = '';
		url.password = '';
		url.search = '';
		url.hash = '';

		return url.toString();
	} catch {
		/*
		 * Relative / protocol-relative / unparseable input: no base to resolve
		 * against, so best-effort strip any authority userinfo (credentials,
		 * e.g. `//user:pass@host`) and the query/hash.
		 */
		return trimmed
			.replace(
				/^((?:[a-z][a-z0-9+.-]*:)?\/\/)[^/?#]*@/i,
				'$1',
			)
			.split(/[?#]/)[0];
	}
}
